import axios from 'axios';
import { endOfDay, isSameDay, startOfMinute, subDays } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { AUTHED_REQUEST_CONFIG } from '../../../store/auth';
import { PERSISTOR_URL } from '../../../store/url';
import { CID, PID } from '../../../store/user';
import { GeofenceType } from '../../Events/types';
import { GeofenceFilter, LabelId } from '../types';
import { SaveResult, SAVE_NOTIFICATION } from '../../../store/notifications';
import { ALL_GEOFENCE_TYPES, ALL_GEOFENCE_ZONES } from '../../../util/constants';
import {
  FenceZone,
  FenceZoneValue,
  GeofenceEntityType,
  isGeofenceEntityType,
} from '../../../util/enums';
import { HeatMapProps } from './Props';
import { VOID, PROMISE_VOID } from '../../../util/typeUtils';
import { MapState } from '../Editor/Props';

export const HeatMapContext = createContext<HeatMapProps>({
  mapState: undefined,
  setMapState: VOID,
  selectedDevices: [],
  setSelectedDevices: VOID,
  selectedLayer: undefined,
  setSelectedLayer: VOID,
  selectedGeofences: [],
  setSelectedGeofences: VOID,
  selectedFenceType: { id: ALL_GEOFENCE_TYPES, value: ALL_GEOFENCE_TYPES },
  setSelectedFenceType: VOID,
  selectedFenceZone: { id: ALL_GEOFENCE_ZONES, value: ALL_GEOFENCE_ZONES },
  setSelectedFenceZone: VOID,
  geofenceFilter: undefined,
  setGeofenceFilter: VOID,
  doHeatmapSearch: PROMISE_VOID,
  data: undefined,
  setData: VOID,
  loadingData: false,
  startDateTime: null,
  setStartDateTime: VOID,
  endDateTime: null,
  setEndDateTime: VOID,
  mapHasInitialised: false,
  setMapHasInitialised: VOID,
});

export type FenceEvent = {
  entered: {
    timestamp?: string;
    coordinates?: { latitude: number; longitude: number };
  };
  exited: {
    timestamp?: string;
    coordinates?: { latitude: number; longitude: number };
  };
  fenceId: {
    id: string;
    label?: string;
    type: string;
  };
  ids: {
    id: { deviceId: string | undefined };
    label?: string;
  };
  layerIds: {
    id: string;
    label: string;
  };
  parentFenceId?: string;
  zone: keyof typeof FenceZone;
};

export type SimplifiedFenceEvent = {
  latitude: number | undefined;
  longitude: number | undefined;
  deviceId: string;
  layerId: string;
  fenceId: {
    id: string;
    label: string;
    type: GeofenceEntityType;
  };
  timestamp?: string;
};

export const applyDefaultEndTimeForDate = (date: Date, now: Date = new Date()): Date =>
  isSameDay(date, now) ? now : endOfDay(date);

export const defaultHeatmapEndDateTime = (now: Date = new Date()): Date =>
  applyDefaultEndTimeForDate(now, now);

export const nextEndDateTime = (
  current: Date | null,
  selected: Date | null,
  now: Date = new Date(),
): Date | null => {
  if (!selected) return null;
  if (!current || !isSameDay(current, selected)) {
    return applyDefaultEndTimeForDate(selected, now);
  }
  return selected;
};

export const buildHeatmapSearchIsoRange = (
  startDateTime: Date | null,
  endDateTime: Date | null,
): { fromIso: string; toIso: string } | null => {
  if (!startDateTime || !endDateTime) return null;
  return {
    fromIso: startOfMinute(startDateTime).toISOString(),
    toIso: startOfMinute(endDateTime).toISOString(),
  };
};

/* Heat Map */
export const HeatMapProvider = ({ children }: { children: ReactNode }) => {
  const cid = useAtomValue(CID);
  const pid = useAtomValue(PID);
  const persistorUrl = useAtomValue(PERSISTOR_URL);
  const authedConfig = useAtomValue(AUTHED_REQUEST_CONFIG);
  const setSaveNotification = useSetAtom(SAVE_NOTIFICATION);

  const [data, setData] = useState<SimplifiedFenceEvent[] | undefined>(undefined);
  const [loadingData, setLoadingData] = useState<boolean>();
  const [geofenceFilter, setGeofenceFilter] = useState<
    (GeofenceFilter & { geofenceIds?: string[] }) | undefined
  >();
  const [mapState, setMapState] = useState<MapState | undefined>();
  const [mapHasInitialised, setMapHasInitialised] = useState<boolean>(false);
  const [selectedDevices, setSelectedDevices] = useState<LabelId[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<LabelId | undefined>(undefined);
  const [selectedGeofences, setSelectedGeofences] = useState<LabelId[]>([]);
  const [selectedFenceType, setSelectedFenceType] = useState<
    GeofenceType | { id: typeof ALL_GEOFENCE_TYPES; value: typeof ALL_GEOFENCE_TYPES }
  >({ id: ALL_GEOFENCE_TYPES, value: ALL_GEOFENCE_TYPES });
  const [selectedFenceZone, setSelectedFenceZone] = useState<
    | { id: FenceZone; value: FenceZoneValue }
    | { id: typeof ALL_GEOFENCE_ZONES; value: typeof ALL_GEOFENCE_ZONES }
  >({ id: ALL_GEOFENCE_ZONES, value: ALL_GEOFENCE_ZONES });
  const [startDateTime, setStartDateTime] = useState<Date | null>(subDays(new Date(), 15));
  const [endDateTime, setEndDateTimeState] = useState<Date | null>(defaultHeatmapEndDateTime());
  const [endDateTimeTouched, setEndDateTimeTouched] = useState(false);

  const setEndDateTime = useCallback(function updateEndDateTimeWithTouchedFlag(
    date: Date | null,
    options?: { touched?: boolean },
  ) {
    setEndDateTimeState(date);
    setEndDateTimeTouched(options?.touched ?? true);
  },
  []);

  const doHeatmapSearch = useCallback(async () => {
    try {
      setData(undefined);
      setLoadingData(true);
      const effectiveEndDateTime = endDateTimeTouched ? endDateTime : defaultHeatmapEndDateTime();
      if (!endDateTimeTouched) {
        setEndDateTime(effectiveEndDateTime, { touched: false });
      }
      const range = buildHeatmapSearchIsoRange(startDateTime, effectiveEndDateTime);
      if (!range) {
        setSaveNotification({
          id: SaveResult.FAIL,
          action: '',
          message: 'Start and end dates are required',
        });
        return;
      }
      const { fromIso, toIso } = range;
      const response = (
        await axios.get<FenceEvent[]>(
          `${persistorUrl}/${cid}/${pid}/analytics/geofence-events/collated?fromIso8601=${fromIso}&toIso8601=${toIso}`,
          authedConfig,
        )
      ).data;

      const enteredBreachZones = response
        .filter(evt => evt.zone === FenceZone.breach)
        .flatMap(breach => {
          if (!isGeofenceEntityType(breach.fenceId.type)) return [];
          return [
            {
              latitude: breach.entered?.coordinates?.latitude,
              longitude: breach.entered?.coordinates?.longitude,
              deviceId: breach.ids.label ?? 'UNKNOWN DEVICE',
              layerId: breach.layerIds.id,
              fenceId: {
                id: breach.fenceId.id,
                label: breach.fenceId.label ?? 'UNKNOWN GEOFENCE',
                type: breach.fenceId.type,
              },
              timestamp: breach.entered.timestamp,
            },
          ];
        });
      const exitedClearedZones = response
        .filter(evt => evt.zone === FenceZone.cleared && evt.exited.coordinates)
        .flatMap(cleared => {
          if (!isGeofenceEntityType(cleared.fenceId.type)) return [];
          return [
            {
              latitude: cleared.exited?.coordinates?.latitude,
              longitude: cleared.exited?.coordinates?.longitude,
              deviceId: cleared.ids.label ?? 'UNKNOWN DEVICE',
              layerId: cleared.layerIds.id,
              fenceId: {
                id: cleared.fenceId.id,
                label: cleared.fenceId.label ?? 'UNKNOWN GEOFENCE',
                type: cleared.fenceId.type,
              },
              timestamp: cleared.exited.timestamp,
            },
          ];
        });

      let filteredData = [...enteredBreachZones, ...exitedClearedZones];
      if (selectedDevices.length > 0) {
        filteredData = filteredData.filter(evt =>
          selectedDevices.find(selectedDevice => evt.deviceId === selectedDevice.id),
        );
      }
      if (selectedLayer) {
        filteredData = filteredData.filter(evt => evt.layerId === selectedLayer.id);
      }
      if (selectedFenceType && selectedFenceType?.id !== ALL_GEOFENCE_TYPES) {
        filteredData = filteredData.filter(evt => evt.fenceId.type === selectedFenceType?.id);
      }
      if (selectedGeofences.length > 0) {
        filteredData = filteredData.filter(evt =>
          selectedGeofences.find(selectedGeofence => evt.fenceId.id === selectedGeofence.id),
        );
      }
      setData(filteredData);
      setSaveNotification({
        id: filteredData.length === 0 ? SaveResult.SUCCESS : SaveResult.FAIL,
        action: '',
        message: `${filteredData.length} ${filteredData.length === 1 ? 'Breach' : 'Breaches'}`,
      });
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  }, [
    startDateTime,
    endDateTime,
    endDateTimeTouched,
    setEndDateTime,
    cid,
    pid,
    persistorUrl,
    authedConfig,
    selectedDevices,
    selectedLayer,
    selectedFenceType,
    selectedGeofences,
    setSaveNotification,
  ]);

  const value = useMemo(
    () => ({
      mapState,
      setMapState,
      selectedDevices,
      setSelectedDevices,
      selectedLayer,
      selectedGeofences,
      selectedFenceType,
      setSelectedFenceType,
      selectedFenceZone,
      setSelectedFenceZone,
      geofenceFilter,
      setGeofenceFilter,
      doHeatmapSearch,
      data,
      setData,
      loadingData,
      setSelectedLayer,
      setSelectedGeofences,
      startDateTime,
      setStartDateTime,
      endDateTime,
      setEndDateTime,
      mapHasInitialised,
      setMapHasInitialised,
    }),
    [
      mapState,
      setMapState,
      selectedDevices,
      setSelectedDevices,
      selectedLayer,
      selectedGeofences,
      selectedFenceType,
      setSelectedFenceType,
      selectedFenceZone,
      setSelectedFenceZone,
      geofenceFilter,
      setGeofenceFilter,
      doHeatmapSearch,
      data,
      setData,
      loadingData,
      setSelectedLayer,
      setSelectedGeofences,
      startDateTime,
      setStartDateTime,
      endDateTime,
      setEndDateTime,
      mapHasInitialised,
      setMapHasInitialised,
    ],
  );
  return <HeatMapContext.Provider value={value}>{children}</HeatMapContext.Provider>;
};

export const useHeatMap = () => useContext(HeatMapContext);
